import React, { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import emailLogService from '../../services/emailLogService';
import Table from '../../components/Common/Table/Table';
import Heading from '../../components/Common/Heading/Heading';
import StatusBadge from '../../components/Common/StatusBadge/StatusBadge';
import SkeletonLoader from '../../components/SkeletonLoader/SkeletonLoader';
import ListScreenSearchBar from '../../components/Common/ListScreenSearchBar/ListScreenSearchBar';
import ListScreenPaginationBar from '../../components/Common/ListScreenPaginationBar/ListScreenPaginationBar';
import { useBreadcrumb } from '../../contexts/BreadcrumbContext';
import './EmailLogs.css';
import '../../components/Common/ListScreenPageLayout/listScreenPage.css';
import { matchesWordStartAny } from '../../utils/searchMatch';

const EmailLogs = ({ embedded = false } = {}) => {
  const [emailLogs, setEmailLogs] = useState([]);
  const [filteredLogs, setFilteredLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  // Default view is latest logs first, so keep index in descending order initially.
  const [sortConfig, setSortConfig] = useState({ key: 'id', direction: 'descending' });

  const location = useLocation();
  const { updateBreadcrumbs, clearBreadcrumbs } = useBreadcrumb();

  // Persist current route for reload
  useEffect(() => {
    sessionStorage.setItem('lastVisitedPath', location.pathname);
  }, [location]);

  useEffect(() => {
    if (embedded) {
      updateBreadcrumbs([
        { label: 'Settings', path: '/settings' },
        { label: 'Email Logs', path: '/settings?tab=email-logs', active: true }
      ]);
    } else {
      updateBreadcrumbs([
        { label: 'Email Logs', path: null, active: true }
      ]);
    }
    return () => {
      clearBreadcrumbs();
    };
  }, [updateBreadcrumbs, clearBreadcrumbs, embedded]);

  const fetchEmailLogs = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await emailLogService.getEmailLogs();

      if (response.success) {
        setEmailLogs(response.data || []);
      } else {
        setError(response.message || 'Failed to fetch email logs');
        toast.error(response.message || 'Failed to fetch email logs');
      }
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to fetch email logs';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, []);

  const filterAndPaginateLogs = useCallback(() => {
    let filtered = [...emailLogs];

    // Apply search filter
    if (searchTerm.trim() !== '') {
      filtered = filtered.filter((log) =>
        matchesWordStartAny(
          [log.from, log.to, log.cc, log.bcc, log.subject, log.body, log.eventName, log.status],
          searchTerm
        )
      );
    }

    // Sorting
    if (sortConfig.key) {
      filtered.sort((a, b) => {
        const aValue = getSortComparableValue(a, sortConfig.key);
        const bValue = getSortComparableValue(b, sortConfig.key);

        if (aValue < bValue) {
          return sortConfig.direction === 'ascending' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'ascending' ? 1 : -1;
        }
        return 0;
      });
    }

    // Apply pagination
    const effectiveItemsPerPage = itemsPerPage === 9999 ? Math.max(filtered.length, 1) : itemsPerPage;
    const startIndex = (currentPage - 1) * effectiveItemsPerPage;
    const endIndex = startIndex + effectiveItemsPerPage;
    const paginated = filtered.slice(startIndex, endIndex);

    setFilteredLogs(paginated);
  }, [emailLogs, searchTerm, currentPage, itemsPerPage, sortConfig]);

  useEffect(() => {
    fetchEmailLogs();
  }, [fetchEmailLogs]);

  useEffect(() => {
    filterAndPaginateLogs();
  }, [filterAndPaginateLogs]);


  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      if (Number.isNaN(date.getTime())) return 'N/A';
      const dd = String(date.getDate()).padStart(2, '0');
      const mm = String(date.getMonth() + 1).padStart(2, '0');
      const yyyy = date.getFullYear();
      return `${dd}-${mm}-${yyyy}`;
    } catch (e) {
      return 'N/A';
    }
  };

  const normalizeStatus = (statusValue) => {
    const raw = String(statusValue || '').trim().toLowerCase();
    if (raw === 'sent' || raw === 'success' || raw === 'active' || raw === 'delivered') return 'Sent';
    if (raw === 'unsent' || raw === 'failed' || raw === 'error' || raw === 'inactive' || raw === 'pending') return 'Unsent';
    return raw ? raw.charAt(0).toUpperCase() + raw.slice(1) : 'Unsent';
  };

  const getSortComparableValue = (row, key) => {
    switch (key) {
      case 'created_at': {
        const ts = new Date(row.created_at || '').getTime();
        return Number.isNaN(ts) ? 0 : ts;
      }
      case 'id':
        return Number(row.id) || 0;
      case 'eventName':
        return formatEventName(row.eventName || '').toLowerCase();
      case 'body':
        return stripHtml(row.body || '').toLowerCase();
      case 'status':
        return normalizeStatus(row.status).toLowerCase();
      default:
        return String(row[key] ?? '').toLowerCase();
    }
  };

  const formatEventName = (eventName) => {
    if (!eventName) return 'N/A';
    return eventName
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  const stripHtml = (html) => {
    if (!html) return '';
    try {
      const doc = new DOMParser().parseFromString(String(html), 'text/html');
      return (doc.body?.textContent || '').replace(/\s+/g, ' ').trim();
    } catch (e) {
      return String(html).replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
    }
  };

  const handleItemsPerPageChange = (value) => {
    setItemsPerPage(value === 'all' ? 9999 : Number(value));
    setCurrentPage(1);
  };

  const requestSort = (key) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };

  const getFilteredTotal = () => {
    if (searchTerm.trim() === '') return emailLogs.length;
    return emailLogs.filter((log) =>
      matchesWordStartAny(
        [log.from, log.to, log.cc, log.bcc, log.subject, log.body, log.eventName, log.status],
        searchTerm
      )
    ).length;
  };

  const totalFiltered = getFilteredTotal();
  const effectiveItemsPerPage = itemsPerPage === 9999 ? Math.max(totalFiltered, 1) : itemsPerPage;
  const totalPages = Math.max(1, Math.ceil(totalFiltered / effectiveItemsPerPage));

  const getDisplayedRange = () => {
    const start = totalFiltered === 0 ? 0 : (currentPage - 1) * effectiveItemsPerPage + 1;
    const end = Math.min(currentPage * effectiveItemsPerPage, totalFiltered);
    return { start, end, total: totalFiltered };
  };

  const { total } = getDisplayedRange();

  const columns = [
    {
      key: 'id',
      title: '#',
      sortable: true,
      render: (_row, rowIndex) => {
        const offset = (currentPage - 1) * effectiveItemsPerPage;
        // Keep serial numbers intuitive when sorting by index/id.
        if (sortConfig.key === 'id' && sortConfig.direction === 'descending') {
          return Math.max(totalFiltered - offset - rowIndex, 1);
        }
        return offset + rowIndex + 1;
      },
      className: 'reusable-table-centered-col'
    },
    { key: 'created_at', title: 'Date', sortable: true, width: '9%', render: (row) => formatDate(row.created_at) },
    { key: 'from', title: 'From', sortable: true, width: '12%', render: (row) => row.from || 'N/A' },
    { key: 'to', title: 'To', sortable: true, width: '12%', render: (row) => row.to || 'N/A' },
    { key: 'cc', title: 'CC', sortable: true, width: '6%', render: (row) => row.cc || 'N/A' },
    { key: 'bcc', title: 'BCC', sortable: true, width: '6%', render: (row) => row.bcc || 'N/A' },
    { key: 'subject', title: 'Subject', sortable: true, width: '12%', render: (row) => row.subject || 'N/A' },
    {
      key: 'body',
      title: 'Body',
      sortable: true,
      width: '32%',
      className: 'email-logs-body-col',
      render: (row) => (
        <div className="email-logs-body-cell">
          <div className="email-logs-body-content">
            {stripHtml(row.body) || 'N/A'}
          </div>
        </div>
      )
    },
    { key: 'eventName', title: 'Event', sortable: true, width: '8%', render: (row) => formatEventName(row.eventName) },
    {
      key: 'status',
      title: 'Status',
      sortable: true,
      width: '5%',
      render: (row) => <StatusBadge status={normalizeStatus(row.status)} />,
      className: 'reusable-table-centered-col'
    }
  ];

  const tableSortBy = sortConfig.key;
  const tableSortOrder = sortConfig.direction === 'ascending' ? 'asc' : 'desc';

  if (loading) {
    return (
      <div className="email-logs-page list-screen-page-container">
        <div className="email-logs-loading-full">
          <div className="email-logs-loading-top">
            <div className="skeleton skeleton-title email-logs-loading-title" />
            <div className="skeleton skeleton-input email-logs-loading-search" />
          </div>
          <div className="email-logs-loading-table">
            <SkeletonLoader type="mediaRows" count={8} />
          </div>
          <div className="email-logs-loading-pagination">
            <div className="skeleton skeleton-input email-logs-loading-page-left" />
            <div className="skeleton skeleton-input email-logs-loading-page-right" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="email-logs-page list-screen-page-container">
      <div className="email-logs-header-flex list-screen-top-bar">
        <div className="list-screen-title-section">
          {!embedded && (
            <Heading title="Email Logs" level="h2" className="email-logs-title list-screen-title-text" />
          )}
        </div>
        <div className="list-screen-actions">
          <ListScreenSearchBar
            value={searchTerm}
            onChange={(val) => {
              setSearchTerm(val);
              setCurrentPage(1);
            }}
            inputId="email-logs-search"
          />
        </div>
      </div>

      {error && (
        <div className="email-logs-error-message">
          <p>{error}</p>
          <button onClick={fetchEmailLogs} className="email-logs-retry-btn">Retry</button>
        </div>
      )}

      <div className="email-logs-container">
        <Table
          columns={columns}
          data={filteredLogs}
          sortBy={tableSortBy}
          sortOrder={tableSortOrder}
          onSort={(key) => requestSort(key)}
          isLoading={loading}
          loadingMode="skeleton"
          skeletonRows={Math.min(effectiveItemsPerPage, 10)}
          emptyMessage={searchTerm ? 'No email logs found matching your search' : 'No email logs found'}
        />
      </div>
      {total > 0 && (
        <div className="email-logs-pagination-outside">
          <ListScreenPaginationBar
            entriesValue={itemsPerPage === 9999 ? 'all' : String(itemsPerPage)}
            onEntriesChange={handleItemsPerPageChange}
            showPageSelector
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
          />
        </div>
      )}
    </div>
  );
};

export default EmailLogs;
